import React from 'react'
import Box from '@mui/material/Box'
import useDisplayDistance from '@/hooks/useDisplayDistance'
import { EmtChip, EmtIcon, EmtRating, EmtTextField, EmtTypography, useMobile } from 'emt-components-v2'
import { useTranslation } from 'next-i18next'
import { useEMTState } from '@/store'
import { GenericProfile } from '@/dataV3/GenericProfile'
import { AnimalProfile } from '@/dataV3/Animal/AnimalProfile'
import { ReviewsSummary } from '@/dataV3/Review/ReviewsSummary'

type Props = React.PropsWithChildren<{
  profile: Pick<GenericProfile, 'displayName' | 'distance' | 'isPremium' | 'isCertified'> & {
    reviewsSummary: Pick<ReviewsSummary, 'totalReviews' | 'averageRating'>
  } & Partial<Pick<AnimalProfile, 'breedSlug'>>
  city?: string
  editName?: boolean
  values?: { displayName: string }
  errors?: { displayName?: string }
  handleChange?: (field: 'displayName', value: string) => void
}>

const UserDetails: React.FC<Props> = ({
  profile: {
    distance,
    displayName,
    reviewsSummary: { totalReviews, averageRating },
    isCertified,
    isPremium,
    breedSlug,
  },
  city,
  editName,
  values,
  errors,
  handleChange,
}) => {
  const { t } = useTranslation('profile')
  const { t: tc } = useTranslation('common')
  const [isMobile] = useMobile()
  const finalDistance = useDisplayDistance()(distance)
  const { dogBreeds, catBreeds, nacBreeds } = useEMTState().configuration
  const breed = breedSlug && [...dogBreeds, ...catBreeds, ...nacBreeds].find((breed) => breed.id === breedSlug)
  const [editableName, setEditableName] = React.useState(false)

  const onEditName = () => {
    setEditableName(true)
  }

  return (
    <Box>
      {/* Title & stars */}
      <Box
        sx={{
          display: 'flex',
          alignItems: editName ? 'center' : 'start',
          justifyContent: 'space-between',
          gap: '30px',
        }}
      >
        {editableName && handleChange && errors && values ? (
          <EmtTextField
            sx={{ width: '550px', maxWidth: '100%' }}
            label="Nom de votre animal"
            value={values.displayName}
            onChange={(e) => handleChange('displayName', e.target.value)}
            error={!!errors.displayName}
            helperText={errors.displayName}
          />
        ) : (
          <EmtTypography
            variant={isMobile ? 'h4' : 'h2'}
            fontWeight={500}
            sx={{ maxWidth: isMobile ? 220 : 650, overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            <b>{displayName}</b>
            {editName && (
              <EmtIcon
                onClick={onEditName}
                className="emt-edit"
                sx={{ fontSize: '16px', marginLeft: '10px', color: '#E8E8EA' }}
              />
            )}
          </EmtTypography>
        )}

        <Box sx={{ display: 'flex', gap: '5px', flexShrink: 0, alignItems: 'center' }}>
          <EmtTypography variant="body1" color="greyGradient.light" lineHeight="1">
            {t('details.review', { count: totalReviews ? totalReviews : 0 })}
          </EmtTypography>
          <EmtRating
            defaultValue={averageRating}
            label={averageRating}
            labelFontSize="h5"
            labelFontColor="greyGradient.light"
            max={1}
            readOnly
            size="large"
            labelFloat="left"
          />
        </Box>
      </Box>
      {/* End Title and stars */}

      {/* Breed, Distance, Premium, Certified */}
      <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: '10px' }}>
        <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          {breed ? (
            <EmtTypography
              variant={'body1'}
              fontWeight={500}
              fontStyle={'italic'}
              sx={{ color: (theme: any) => theme.palette.greyGradient[250] }}
            >
              {breed.name}
            </EmtTypography>
          ) : null}
          {distance || city ? (
            <Box sx={{ display: 'flex' }}>
              <EmtIcon className="emt-pin-1" sx={{ color: 'greyGradient.450', fontWeight: 600 }} fontSize="small" />
              <EmtTypography variant="body2" color="greyGradient.450">
                {finalDistance || city}
              </EmtTypography>
            </Box>
          ) : null}
        </Box>

        <Box sx={{ display: 'flex', gap: '12px' }}>
          {isCertified && <EmtChip label={tc('profileCard.idChecked')} color="successlight" sx={{ fontWeight: 600 }} />}
          {isPremium && (
            <EmtChip
              label={tc('profileCard.premium')}
              color="secondarylight"
              icon={<EmtIcon className="emt-crown" sx={{ fontSize: '12px' }} />}
              sx={{ fontWeight: 600 }}
            />
          )}
        </Box>
      </Box>
      {/* Type, Distance, Premium, Certified */}
    </Box>
  )
}

export const UserDetailsLoading: React.FC = () => <div />

export default UserDetails
